import { allocateRowSlots, isAnomaly, type PivotRow } from './evalRelationsPivot';
import type { ColDef } from './poolColDefs';

const SOURCE_LABELS = {
  ORG_AUTO: '组织自动',
  MANUAL: '人工',
  MANUAL_OVERRIDE: '人工覆盖',
} as const;

export function taskSourceLabel(source: keyof typeof SOURCE_LABELS | undefined): string {
  return source ? SOURCE_LABELS[source] : '来源未返回';
}

export function csvField(value: unknown): string {
  const text = value == null ? '' : String(value);
  if (text.includes(',') || text.includes('"') || text.includes('\n')) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

interface BuildCsvOptions {
  poolOrder: string[];
  allRows: PivotRow[];
  getColDefs: (pool: string) => ColDef[];
  getTalentPoolLabel: (pool: string) => string;
  acks: Set<number>;
  notes: Record<number, string>;
}

export function buildEvalRelationsCsv({
  poolOrder,
  allRows,
  getColDefs,
  getTalentPoolLabel,
  acks,
  notes,
}: BuildCsvOptions): string {
  const lines: string[] = [];
  for (const pool of poolOrder) {
    const rows = allRows.filter((row) => row.pool === pool);
    const cols = getColDefs(pool);
    lines.push(`# ${getTalentPoolLabel(pool)} (${rows.length} 人)`);
    lines.push(
      [
        '序号',
        '被评价人姓名',
        '部门',
        '职位',
        ...cols.map((col) => col.label),
        '任务来源/状态',
        '覆盖/排除原因',
        '组织异常',
      ]
        .map(csvField)
        .join(','),
    );
    rows.forEach((row, index) => {
      const fields = [
        String(index + 1),
        row.evaluatee.name,
        row.evaluatee.deptPath || '',
        row.evaluatee.position || '',
      ];
      const noteParts: string[] = [];
      const sourceParts: string[] = [];
      const reasonParts: string[] = [];
      const allocation = allocateRowSlots(row, cols);
      cols.forEach((col, colIndex) => {
        const evaluators = allocation.perCol[colIndex];
        fields.push(evaluators.map((evaluator) => evaluator.evaluatorName).join('、'));
        if (!evaluators.length) {
          noteParts.push(`${col.label}: 缺失评价人（待补派）`);
        }
        evaluators.forEach((evaluator) => {
          sourceParts.push(
            `${col.label}/${evaluator.evaluatorName}: ${taskSourceLabel(evaluator.source)} / ${evaluator.status}`,
          );
          if (evaluator.overrideReason) {
            reasonParts.push(`${col.label}/${evaluator.evaluatorName}: ${evaluator.overrideReason}`);
          }
          if (isAnomaly(row, col, evaluator, acks)) {
            const note = notes[evaluator.taskId];
            noteParts.push(
              `${col.label}: ${evaluator.evaluatorName} 与花名册不一致${note ? `（${note}）` : ''}`,
            );
          }
        });
      });
      if (allocation.unassigned.length) {
        noteParts.push(
          `未归类: ${allocation.unassigned
            .map(
              (evaluator) =>
                `${evaluator.evaluatorName}（${evaluator.roleInEval}，不在该池模板）`,
            )
            .join('；')}`,
        );
      }
      fields.push(sourceParts.join('；'), reasonParts.join('；'), noteParts.join('；'));
      lines.push(fields.map(csvField).join(','));
    });
    lines.push('');
  }
  return `\ufeff${lines.join('\n')}`;
}
